import { getTimeZoneOffsetMinutes } from "./clientEnv.ts";

/**
 * Kiosk-local time for the `KioskTime` / `KioskDate` grid DisplayTypes.
 *
 * Those columns hold the SQL server's clock, like every other datetime — the point
 * of the DisplayType is to answer "what time was it *at the kiosk*", which means a
 * second conversion out of the server's zone and into the kiosk's.
 *
 * The zone comes from the location, not the kiosk: kiosk → LocationID → location →
 * `TimeZoneIana`, translated from the Windows-style `sTimeZone` name server-side so
 * Intl can use it. That mirrors WebAdmin2's chain (kiosk → location → TimeZones)
 * but leans on the browser's own DST rules instead of the S_Admin_TimeZones DST
 * range rows, so it stays correct for values in any year.
 *
 * The abbreviation label still comes from the S_Admin_TimeZones cache, since that
 * is where "CET"/"CEST" are defined — Intl's own short names render as "GMT+2" in
 * most locales. Which of the pair applies is decided by comparing the zone's offset
 * at that instant against its standard-time offset.
 */

export interface KioskZoneInfo {
  /** IANA id to format with. */
  iana: string;
  /** "CET" / "CEST" style label, or "" when the cache has no abbreviation. */
  abbreviation: string;
}

/** Resolves a kiosk id to its zone, or null when anything in the chain is missing. */
export type KioskZoneResolver = (kioskId: number, at: Date) => KioskZoneInfo | null;

let resolver: KioskZoneResolver = () => null;

export function setKioskZoneResolver(fn: KioskZoneResolver): void {
  resolver = fn;
}

export function resolveKioskZone(kioskId: number, at: Date): KioskZoneInfo | null {
  try {
    return resolver(kioskId, at);
  } catch {
    return null;
  }
}

/**
 * True when `zone` is observing daylight saving at `at`. Compares the offset then
 * against the zone's minimum offset across the year, which is standard time for
 * every real zone (southern-hemisphere zones included — the minimum is still the
 * non-DST side).
 */
export function isInDaylightSaving(zone: string, at: Date): boolean {
  const year = at.getUTCFullYear();
  const jan = getTimeZoneOffsetMinutes(zone, new Date(Date.UTC(year, 0, 15)));
  const jul = getTimeZoneOffsetMinutes(zone, new Date(Date.UTC(year, 6, 15)));
  const standard = Math.min(jan, jul);
  return getTimeZoneOffsetMinutes(zone, at) > standard;
}

/**
 * Builds a resolver over the InitCache collections. Kept here rather than in
 * AuthProvider so the lookup maps are built once per cache rather than per cell.
 */
export function buildKioskZoneResolver(
  kiosks: Array<{ KioskID: number; LocationID: number }> | undefined,
  locations: Array<{ LocationID: number; TimeZone?: string; TimeZoneIana?: string }> | undefined,
  timeZones: Array<{ Name: string; Abbreviation?: string; AbbreviationDST?: string }> | undefined,
): KioskZoneResolver {
  if (!kiosks?.length || !locations?.length) return () => null;

  const locationByKiosk = new Map<number, number>();
  for (const k of kiosks) locationByKiosk.set(k.KioskID, k.LocationID);

  const zoneByLocation = new Map<number, { iana: string; name: string }>();
  for (const l of locations) {
    if (l.TimeZoneIana) zoneByLocation.set(l.LocationID, { iana: l.TimeZoneIana, name: l.TimeZone ?? "" });
  }

  // Several rows share a name (one per DST period); the abbreviations repeat, so
  // first wins.
  const abbrByName = new Map<string, { std: string; dst: string }>();
  for (const z of timeZones ?? []) {
    if (!z?.Name || abbrByName.has(z.Name.toLowerCase())) continue;
    abbrByName.set(z.Name.toLowerCase(), { std: z.Abbreviation ?? "", dst: z.AbbreviationDST ?? "" });
  }

  return (kioskId, at) => {
    const locationId = locationByKiosk.get(kioskId);
    if (locationId === undefined) return null;
    const zone = zoneByLocation.get(locationId);
    if (!zone) return null;
    const pair = abbrByName.get(zone.name.toLowerCase());
    let abbreviation = "";
    if (pair) {
      abbreviation = isInDaylightSaving(zone.iana, at) ? (pair.dst || pair.std) : (pair.std || pair.dst);
    }
    return { iana: zone.iana, abbreviation };
  };
}
